import {
    type AllTags,
    accommodationTags,
    type CategorySpecificTags,
    diningTags,
    eventTags,
    flightTags,
    localExperienceTags,
    nightlifeTags,
    type OfferingCategory,
    servicesTags,
    shoppingTags,
    Tag,
    type TagCategory,
    type TagMap,
    Tags,
    transportationTags,
    travelItineraryTags,
    wellnessTags,
} from "@albertine/types";
import { tagDisplayName } from "albertine-shared-web";

import type { CategoriesWithUncategorized } from "../types/OfferingCategory";
import { sortByStringComparator } from "./sort.util";
import { isTypeOf } from "./zod.util";

export function getCategorySpecificTags(
    offeringCategory: CategoriesWithUncategorized | undefined,
): CategorySpecificTags {
    if (!offeringCategory) return {};
    switch (offeringCategory) {
        case "dining":
            return diningTags;
        case "nightlife":
            return nightlifeTags;
        case "flight":
            return flightTags;
        case "train":
            return {};
        case "accommodation":
            return accommodationTags;
        case "travelItinerary":
            return travelItineraryTags;
        case "transportation":
            return transportationTags;
        case "event":
            return eventTags;
        case "localExperience":
            return localExperienceTags;
        case "shopping":
            return shoppingTags;
        case "wellness":
            return wellnessTags;
        case "services":
            return servicesTags;
        case "uncategorized":
            return {};
        default:
            return {};
    }
}

export function flattenTags(tags: CategorySpecificTags | AllTags) {
    return Object.values(tags).reduce(
        (tagsArray: string[], tagMap: TagMap) => [
            ...tagsArray,
            ...Object.values(tagMap),
        ],
        [],
    );
}

export function allowCategorySpecificTagsOnly(
    tags: string[] | Tag[],
    offeringCategory: CategoriesWithUncategorized | undefined,
): Tag[] {
    const categorySpecificTags = getCategorySpecificTags(offeringCategory);
    const allowedTagsPerCategory = flattenTags(categorySpecificTags);
    return tags.filter((tag) => allowedTagsPerCategory.includes(tag)) as Tag[];
}

export function getSelectableTagFilters(
    offeringCategories: OfferingCategory[] | undefined,
): CategorySpecificTags {
    return Object.fromEntries(
        Object.entries(Tags)
            .map((_tags) => {
                const tagCategory = _tags[0] as TagCategory;
                const categoryTags = _tags[1];
                const tagIds = Object.values(categoryTags);

                if (tagCategory !== "price") {
                    const sortAlphabetically = (a: any, b: any) =>
                        sortByStringComparator<Tag>((tag) =>
                            tag ? tagDisplayName(tag, "en") : "",
                        )(a, b);
                    tagIds.sort(sortAlphabetically);
                }

                const categorySpecificTagsAvailable = offeringCategories?.map(
                    (category) => flattenTags(getCategorySpecificTags(category)),
                );

                const tagsAvailableForSelection = tagIds.filter((tag) => {
                    if (!categorySpecificTagsAvailable) return true;

                    const isTagInCategory = categorySpecificTagsAvailable.some(
                        (selectedCategoryTags) => selectedCategoryTags.includes(tag),
                    );
                    return isTagInCategory;
                });

                return [tagCategory, tagsAvailableForSelection];
            })
            .filter((tags) => tags[1].length > 0),
    );
}

export function sortTags(tags: string[]): Tag[] {
    const priceOrder = [
        Tags.price.veryExpensive,
        Tags.price.expensive,
        Tags.price.moderate,
        Tags.price.inexpensive,
        Tags.price.free,
    ];
    return tags
        .filter((it): it is Tag => !!isTypeOf(it, Tag))
        .sort((a, b) => {
            // Handle price sorting separately
            if (a.includes("price:") && b.includes("price:")) {
                const aIndex = priceOrder.findIndex((order) => order === a);
                const bIndex = priceOrder.findIndex((order) => order === b);
                return aIndex - bIndex;
            }
            // Default alphabetical sorting for other tags
            return a.localeCompare(b);
        });
}

export function splitTag(tag: string): { category: string; tag: string } | null {
    const components: string[] = tag.split(":");
    if (components.length !== 2) return null;
    const category: string = components[0];
    const tagValue: string = components[1];
    return { category, tag: tagValue };
}

export function separateTagsByCategory(tags: string[]): Record<string, Tag[]> {
    const sortedTags = tags.sort((a, b) => {
        const isTypeA = a.startsWith("type:");
        const isTypeB = b.startsWith("type:");
        const isPriceA = a.startsWith("price:");
        const isPriceB = b.startsWith("price:");

        if (isTypeA) return -1;
        if (isTypeB) return 1;
        if (isPriceA) return 1;
        if (isPriceB) return -1;
        return a.localeCompare(b);
    });

    return sortedTags.reduce((categoryDict: Record<string, Tag[]>, tag: string) => {
        const splitted = splitTag(tag);
        if (splitted && isTypeOf(tag, Tag)) {
            const { category } = splitted;

            categoryDict[category] = sortTags([...(categoryDict[category] ?? []), tag]);
        }
        return categoryDict;
    }, {});
}
